import { Component, OnDestroy, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProfileService } from '../../../auth/services/profile.service';
import { PropertyInputComponent } from '../../components/input-options/components/property-input/property-input.component';
import { OptionProperty } from '../../components/input-options/models/option-property';
import { PropertyType } from '../../components/input-options/models/property-type.enum';
import { IProperty } from '../../components/input-options/models/property.interface';
import { TextProperty } from '../../components/input-options/models/text-property';
import { UnsavedChangesComponent } from '../../services/unsaved-changes/unsaved-changes.component';
import { IFeedbackData } from '../service/feedback-modal.service';
import { FeedbackCategory } from './models/feedback-category.enum';

@Component({
  selector: 'app-feedback-modal',
  templateUrl: './feedback-modal.component.html',
  styleUrls: ['./feedback-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, PropertyInputComponent],
})
export class FeedbackModalComponent extends UnsavedChangesComponent implements OnDestroy {
  protected submitFeedbackForm = viewChild.required<NgForm>('submitFeedbackForm');
  protected feedbackCommand: Record<string, unknown> = {
    category: FeedbackCategory.Compliment,
    title: '',
    description: '',
  };

  protected currentUrl: string;
  protected organizationId: string = '';
  protected properties: IProperty[] = [
    {
      key: 'category',
      label: 'Categorie',
      required: true,
      description: 'De categorie van de feedback',
      type: PropertyType.Option,
      options: [
        { key: FeedbackCategory.Remark, label: 'Opmerking' },
        { key: FeedbackCategory.Roadmap, label: 'Idee voor de roadmap' },
        { key: FeedbackCategory.Bug, label: 'Bug' },
        { key: FeedbackCategory.Complaint, label: 'Klacht' },
        { key: FeedbackCategory.Compliment, label: 'Compliment' },
      ],
    } as OptionProperty,
    {
      key: 'title',
      label: 'Titel',
      description: 'Een korte titel voor je feedback',
      required: true,
      type: PropertyType.Text,
    } as TextProperty,
    {
      key: 'description',
      label: 'Beschrijving',
      description: 'Een uitgebreide beschrijving van je feedback',
      required: true,
      type: PropertyType.MultiText,
      maxLength: 1000,
    } as TextProperty,
  ];

  private organizationSubscription?: Subscription;
  private router = inject(Router);
  private profileService = inject(ProfileService);

  constructor() {
    super();
    this.currentUrl = this.router.url;

    // Subscribe to get the organization ID
    this.organizationSubscription = this.profileService.getCurrentOrganizationId().subscribe((id) => {
      this.organizationId = id ?? '';
    });
  }

  public form = (): NgForm => this.submitFeedbackForm();

  ngOnDestroy(): void {
    if (this.organizationSubscription) {
      this.organizationSubscription.unsubscribe();
    }
  }

  protected validateAndSubmit(): void {
    if (!this.hasRequiredFields()) {
      return;
    }

    this.onSubmit();
  }

  protected hasRequiredFields(): boolean {
    // Check other required properties
    return this.properties
      .filter((p) => p.required)
      .every((p) => this.feedbackCommand[p.key] != null && this.feedbackCommand[p.key] !== '');
  }

  private onSubmit(): void {
    const feedbackData: IFeedbackData = {
      category: this.feedbackCommand['category'] as FeedbackCategory,
      title: this.feedbackCommand['title'] as string,
      description: this.feedbackCommand['description'] as string,
      path: this.currentUrl,
      organizationId: this.organizationId,
    };

    // Close modal with the feedback data
    this.close(feedbackData);
  }
}
